import { useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";

/**
 * Three quotes side by side from md up. On a phone that stack ran to nearly three
 * screens of scrolling, so below md the same markup becomes a swipeable carousel
 * that rotates on its own. Embla is switched off by breakpoint rather than the
 * slides being rendered twice, so there is one copy of each quote in the DOM.
 *
 * Reveal sits inside the slide rather than being the slide. Embla repositions
 * slides in loop mode by writing an inline transform and expecting it to land
 * instantly; .reveal puts a 0.7s transform transition on whatever element carries
 * it, which turned every wrap into a visible slide flying across the viewport.
 */
const testimonials = [
  {
    quote: "Your pod was a lifesaver.",
    name: "Peter Greenberg",
    org: "CBS News",
  },
  {
    quote:
      "I was planning on taking a taxi back to my office for an important Zoom and then come back to the conference, this really saved me.",
    name: "Lindsey Carnett",
    org: "Marketing Maven",
  },
  {
    quote: "I can't believe how soundproof it was, great product.",
    name: "Pete Van Dorn",
    org: "Pay Rewards",
  },
];

/** Long enough to read the longest quote without feeling parked. */
const ROTATE_MS = 7000;

const TestimonialsSection = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    breakpoints: { "(min-width: 768px)": { active: false } },
  });
  const [selected, setSelected] = useState(0);
  const [isPhone, setIsPhone] = useState(false);
  const [stopped, setStopped] = useState(false);

  /**
   * Raised just before a timer-driven advance so the select handler can tell an
   * automatic move from one the visitor made. Rotation used to stop on Embla's
   * pointerDown, which also fires when a finger starts a vertical page scroll
   * anywhere over the carousel, so it died on the first scroll past.
   */
  const advancing = useRef(false);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelected(emblaApi.selectedScrollSnap());
      if (advancing.current) {
        advancing.current = false;
      } else {
        // A swipe or a dot. The visitor is driving now, so stop rotating.
        setStopped(true);
      }
    };

    setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  // Mirrors the md breakpoint, so rotation is never driven while the grid is showing.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsPhone(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!emblaApi || !isPhone || stopped) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const id = window.setInterval(() => {
      advancing.current = true;
      emblaApi.scrollNext();
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [emblaApi, isPhone, stopped]);

  return (
    <section id="reviews" className="py-20 md:py-28 bg-secondary">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHeading eyebrow="From our guests" title="What people say after an hour inside" />

        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex touch-pan-y md:grid md:grid-cols-3 md:gap-8 lg:gap-10">
            {testimonials.map((t, i) => (
              <div key={t.name} className="min-w-0 flex-[0_0_100%] md:flex-none">
                <Reveal delay={i * 90} className="h-full">
                  <figure className="flex flex-col h-full border-t border-quietcove-blue/15 pt-7">
                    <span
                      aria-hidden="true"
                      className="font-display text-5xl leading-none text-gold/45 mb-4 select-none"
                    >
                      &ldquo;
                    </span>
                    <blockquote className="font-display text-lg lg:text-xl text-quietcove-blue leading-[1.5] tracking-[-0.01em] grow">
                      {t.quote}
                    </blockquote>
                    <figcaption className="mt-7">
                      <div className="font-semibold text-quietcove-blue">{t.name}</div>
                      <div className="text-sm text-quietcove-grey mt-0.5">{t.org}</div>
                    </figcaption>
                  </figure>
                </Reveal>
              </div>
            ))}
          </div>
        </div>

        {/* Phone only: the grid needs no controls. */}
        <div className="flex md:hidden justify-center items-center gap-1.5 mt-9">
          {testimonials.map((t, i) => (
            <button
              key={t.name}
              type="button"
              onClick={() => emblaApi?.scrollTo(i)}
              aria-label={`Show the quote from ${t.name}`}
              aria-current={i === selected}
              className="group h-8 px-1.5 flex items-center"
            >
              <span
                className={`block h-2 rounded-full transition-all duration-500 ${
                  i === selected
                    ? "w-7 bg-gold"
                    : "w-2 bg-quietcove-blue/20 group-hover:bg-quietcove-blue/40"
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
